/**
 * The assistant's navigation allowlist: the only in-app pages a reply may
 * link to. Keys map to routes that exist in App.tsx; nothing from the model
 * is ever used as a URL. A `[[nav:KEY|Label]]` marker with an unknown key is
 * collapsed to its label text, so a model can't fabricate a link.
 */

export const NAV_TARGETS = {
  dashboard: { path: '/dashboard', label: 'Open dashboard' },
  plots: { path: '/plots', label: 'Open plots' },
  contracts: { path: '/contracts', label: 'Open leases' },
  invoices: { path: '/invoices', label: 'Open invoices' },
  payments: { path: '/payments', label: 'Open payments' },
  contacts: { path: '/contacts', label: 'Open contacts' },
  leads: { path: '/leads', label: 'Open leads' },
  proposals: { path: '/proposals', label: 'Open proposals' },
  schedule: { path: '/schedule', label: 'Open schedule' },
  crops: { path: '/crops', label: 'Open crops' },
  'crop-prices': { path: '/crop-prices', label: 'Open crop prices' },
  'tax-reporting': { path: '/tax-reporting', label: 'Open tax reporting' },
} as const;

export type NavTargetKey = keyof typeof NAV_TARGETS;

export interface NavAction {
  target: NavTargetKey;
  label: string;
}

const MAX_ACTIONS = 2;

export function isNavTarget(key: string): key is NavTargetKey {
  return Object.prototype.hasOwnProperty.call(NAV_TARGETS, key);
}

export function pathForTarget(key: NavTargetKey): string {
  return NAV_TARGETS[key].path;
}

export function navAction(target: NavTargetKey): NavAction {
  return { target, label: NAV_TARGETS[target].label };
}

/** Human page name for a pathname, used to tell the model where the user is. */
export function pageLabelForPath(pathname: string): string | undefined {
  const first = pathname.split('/').filter(Boolean)[0];
  if (!first) return undefined;
  if (!isNavTarget(first)) return undefined;
  return NAV_TARGETS[first].label.replace(/^Open /, '').replace(/^\w/, (c) => c.toUpperCase());
}

const NAV_MARKER_RE = /\[\[nav:([^|\]]+)\|([^\]]+)\]\]/g;

/** Pulls validated nav markers out of a reply; the label stays in the text. */
export function extractNavActions(reply: string): { text: string; actions: NavAction[] } {
  const actions: NavAction[] = [];
  const text = reply.replace(NAV_MARKER_RE, (_m, rawKey: string, rawLabel: string) => {
    const key = rawKey.trim();
    const label = rawLabel.trim();
    if (isNavTarget(key) && !actions.some((a) => a.target === key)) {
      actions.push({ target: key, label });
    }
    return label;
  });
  return { text, actions };
}

// Ordered by specificity: money questions should land on invoices before
// a generic "plot" mention sends them to plots.
const KEYWORD_RULES: Array<[RegExp, NavTargetKey]> = [
  [/\b(overdue|owe|owing|arrears|unpaid|invoice|bill(ed|ing)?|debt|outstanding)/i, 'invoices'],
  [/\b(payment|paid|pay(s|ing)?\b|receipt|collect)/i, 'payments'],
  [/\b(lease|contract|tenanc|renew|expir)/i, 'contracts'],
  [/\b(plot|land|farm ?land|vacan|occupan|soil|hectare)/i, 'plots'],
  [/\b(lead|enquir|inquir)/i, 'leads'],
  [/\bproposal/i, 'proposals'],
  [/\b(schedule|calendar|task|activit|meeting|inspection)/i, 'schedule'],
  [/\b(crop price|market price|price of)/i, 'crop-prices'],
  [/\b(tax|vat|wht|withholding)/i, 'tax-reporting'],
  [/\bcontacts?\b/i, 'contacts'],
];

/** Picks up to two relevant pages from the question when the reply has none. */
export function suggestNavActions(question: string): NavAction[] {
  const targets: NavTargetKey[] = [];
  for (const [re, target] of KEYWORD_RULES) {
    if (targets.length >= MAX_ACTIONS) break;
    if (re.test(question) && !targets.includes(target)) targets.push(target);
  }
  return targets.map(navAction);
}
